import React, { useState, useEffect, useContext, useMemo } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Formik } from "formik";
import axios from "axios";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Paper as MuiPaper,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import Select from "./SelectBox";
import { GlobalContext } from "../contexts/GlobalContext";
import {
  filterCountriesForProduct,
  getCountryProductHelperText,
  isCountryValidForProduct,
} from "../utils/countryProductUtils";

const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

// Helper function to get user's locale decimal separator
const getDecimalSeparator = () => {
  try {
    // Use Intl.NumberFormat to detect the decimal separator for the user's locale
    const numberFormat = new Intl.NumberFormat(navigator.language || "en-US");
    const parts = numberFormat.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch (error) {
    // Fallback to dot if detection fails
    return ".";
  }
};

// Helper function to convert comma to dot and parse to float
const parseDecimalValue = (value) => {
  if (!value || value === "") return 0;
  const normalizedValue = String(value).replace(",", ".");
  return parseFloat(normalizedValue) || 0;
};

// Helper function to normalize input (allow both comma and dot)
const normalizeDecimalInput = (value) => {
  if (!value) return "";
  return String(value).replace(",", ".");
};

// Helper function to format value with 2 decimals using user's locale separator
const formatWithTwoDecimals = (value) => {
  if (!value && value !== 0) {
    const separator = getDecimalSeparator();
    return `0${separator}00`;
  }
  const numValue = typeof value === "string" ? parseDecimalValue(value) : value;
  const formatted = numValue.toFixed(2);
  const separator = getDecimalSeparator();
  // Replace dot with user's locale separator
  return separator === "," ? formatted.replace(".", ",") : formatted;
};

const initialValues = {
  productId: "",
  countryId: "",
};

const validationSchema = Yup.object().shape({
  productId: Yup.number().required("Required").oneOf([1, 2], "Must be VAT (1) or Excise (2)"),
  countryId: Yup.string()
    .required("Required")
    .test(
      "country-product-match",
      "Selected country does not match the product type",
      function validateCountryForProduct(value) {
        if (!value || !this.parent.productId) {
          return true;
        }

        return isCountryValidForProduct(value, this.parent.productId);
      }
    ),
});

const AddRefundRatesForm = ({ refreshTable, countryData, setNotify }) => {
  const { globalData } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rates, setRates] = useState([]);
  const [newRate, setNewRate] = useState({ fromRate: "", toRate: "", monthFee: "" });
  const [bulkImportText, setBulkImportText] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    if (!globalData?.userId) {
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "User information not available",
          type: "error",
        });
      }
      setSubmitting(false);
      return;
    }

    if (rates.length === 0) {
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "Please add at least one refund rate",
          type: "error",
        });
      }
      setSubmitting(false);
      return;
    }

    try {
      // Validate that we have required fields
      if (!values.countryId || !values.productId) {
        if (setNotify) {
          setNotify({
            isOpen: true,
            message: "Please select a country and product",
            type: "error",
          });
        }
        setSubmitting(false);
        return;
      }

      // Create all rates for this country/product
      const results = [];
      for (const rate of rates) {
        try {
          const payload = {
            countryId: parseInt(values.countryId),
            productId: parseInt(values.productId),
            fromRate: parseDecimalValue(rate.fromRate),
            toRate: parseDecimalValue(rate.toRate),
            monthFee: parseDecimalValue(rate.monthFee || "0"),
            quarterFee: parseDecimalValue(rate.monthFee || "0") * 3,
            yearFee: parseDecimalValue(rate.monthFee || "0") * 12,
            createdBy: globalData.userId,
          };
          
          console.log("Creating rate with payload:", payload);
          const response = await axios.post("/api/v1/refundrates", payload);
          results.push(response);
          console.log("Rate created successfully:", response.data);
        } catch (rateError) {
          console.error("Error creating individual rate:", rateError);
          console.error("Rate data:", rate);
          throw rateError; // Re-throw to be caught by outer catch
        }
      }

      console.log(`All ${results.length} rates created successfully`);

      if (setNotify) {
        setNotify({
          isOpen: true,
          message: `${results.length} rate(s) added successfully`,
          type: "success",
        });
      }
      resetForm();
      setRates([]);
      setNewRate({ fromRate: "", toRate: "", monthFee: "" });
      setBulkImportText("");
      setShowBulkImport(false);
      setSelectedProduct(null);
      setOpen(false);
      refreshTable();
    } catch (error) {
      console.error("Error creating refund rates:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      const errorMsg = error.response?.data?.message || 
                      (typeof error.response?.data === 'string' ? error.response.data : '') ||
                      error.message || 
                      "Unknown error occurred";
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "Error: " + errorMsg,
          type: "error",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRate = () => {
    if (newRate.fromRate && newRate.toRate) {
      setRates([
        ...rates,
        {
          fromRate: newRate.fromRate,
          toRate: newRate.toRate,
          monthFee: newRate.monthFee || "0.00",
        },
      ]);
      setNewRate({ fromRate: "", toRate: "", monthFee: "" });
    }
  };

  // Helper function to clean and parse numeric value from Excel paste
  const cleanNumericValue = (value) => {
    if (!value) return "0";
    
    // Remove € symbol and spaces
    let cleaned = value.trim().replace(/[€\s]/g, "");
    
    // Check if comma is used as decimal separator (European format)
    // If there's a comma, it's likely the decimal separator
    // Remove all dots (thousand separators) and replace comma with dot
    if (cleaned.includes(",")) {
      // European format: 1.050,00 or 9.999.999,00
      cleaned = cleaned.replace(/\./g, ""); // Remove thousand separators (dots)
      cleaned = cleaned.replace(",", "."); // Replace comma with dot for decimal
    } else if (cleaned.includes(".")) {
      // Check if it's a decimal separator or thousand separator
      const parts = cleaned.split(".");
      if (parts.length === 2 && parts[1].length <= 2) {
        // Likely decimal separator (e.g., 35.01)
        // Keep as is
      } else {
        // Likely thousand separators (e.g., 1.050.00 or 9.999.999.00)
        // Remove all dots
        cleaned = cleaned.replace(/\./g, "");
      }
    }
    
    return cleaned;
  };

  const handleBulkImport = () => {
    if (!bulkImportText.trim()) {
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "Please paste data to import",
          type: "error",
        });
      }
      return;
    }

    try {
      const lines = bulkImportText.trim().split(/\r?\n/);
      const importedRates = [];

      for (const line of lines) {
        if (!line.trim()) continue;

        // Split by tab or multiple spaces
        let columns = line.split(/\t/).filter((col) => col.trim());
        
        if (columns.length < 2) {
          // Try splitting by multiple spaces
          columns = line.split(/\s{2,}/).filter((col) => col.trim());
        }
        
        if (columns.length < 2) {
          // Try splitting by single space if tab didn't work
          const spaceSplit = line.trim().split(/\s+/);
          if (spaceSplit.length >= 2) {
            columns = spaceSplit;
          }
        }

        if (columns.length < 2) {
          continue; // Skip invalid lines
        }

        const fromRate = cleanNumericValue(columns[0]);
        const toRate = cleanNumericValue(columns[1]);
        const monthFee = columns.length > 2 ? cleanNumericValue(columns[2]) : "0";

        if (fromRate && toRate) {
          importedRates.push({
            fromRate: formatWithTwoDecimals(parseDecimalValue(fromRate)),
            toRate: formatWithTwoDecimals(parseDecimalValue(toRate)),
            monthFee: formatWithTwoDecimals(parseDecimalValue(monthFee)),
          });
        }
      }

      if (importedRates.length === 0) {
        if (setNotify) {
          setNotify({
            isOpen: true,
            message: "No valid rates found in pasted data",
            type: "error",
          });
        }
        return;
      }

      setRates([...rates, ...importedRates]);
      setBulkImportText("");
      setShowBulkImport(false);
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: `${importedRates.length} rate(s) imported successfully`,
          type: "success",
        });
      }
    } catch (error) {
      console.error("Error parsing bulk import:", error);
      if (setNotify) {
        setNotify({
          isOpen: true,
          message: "Error parsing bulk import data",
          type: "error",
        });
      }
    }
  };

  const handleRemoveRate = (index) => {
    setRates(rates.filter((_, i) => i !== index));
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedProduct(null);
    setRates([]);
    setNewRate({ fromRate: "", toRate: "", monthFee: "" });
    setBulkImportText("");
    setShowBulkImport(false);
  };

  const countryOptions = useMemo(() => {
    const filteredCountries = filterCountriesForProduct(countryData, selectedProduct);
    return filteredCountries.map((country) => ({
      key: country.countryId,
      value: `${country.countryCode} - ${country.countryName}`,
    }));
  }, [countryData, selectedProduct]);

  return (
    <Card mb={0}>
      <CardContent>
        <Paper mt={0}>
          <div>
            <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
              Add Refund Rates
            </Button>
            <Dialog open={open} onClose={handleCloseDialog} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth>
              <DialogTitle id="form-dialog-title">Add Refund Rates</DialogTitle>
              <Divider />
              <DialogContent>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                  {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => (
                    <Card mb={6}>
                      <CardContent>

                        {isSubmitting ? (
                          <Box display="flex" justifyContent="center" my={20} mx={40}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <form onSubmit={handleSubmit}>
                            <Grid container spacing={6}>
                              <Grid item md={12} xs={12}>
                                <Select
                                  name="productId"
                                  label="Product"
                                  required={true}
                                  options={[
                                    { key: 1, value: "VAT" },
                                    { key: 2, value: "Excise" },
                                  ]}
                                  onChange={(event) => {
                                    handleChange(event);
                                    const productId = event.target.value;
                                    setSelectedProduct(productId);
                                    if (!isCountryValidForProduct(values.countryId, productId)) {
                                      setFieldValue("countryId", "");
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item md={12} xs={12}>
                                <Select
                                  name="countryId"
                                  label="Country"
                                  options={countryOptions}
                                  required={true}
                                  disabled={!values.productId}
                                  helperText={getCountryProductHelperText(values.productId)}
                                />
                              </Grid>
                            </Grid>

                            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                              Refund Rates
                            </Typography>

                            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setShowBulkImport(!showBulkImport)}
                                size="small"
                              >
                                {showBulkImport ? "Hide Bulk Import" : "Bulk Import"}
                              </Button>
                            </Box>

                            {showBulkImport && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" gutterBottom>
                                  Paste data from Excel (FromRate, ToRate, MonthFee):
                                </Typography>
                                <TextField
                                  multiline
                                  rows={6}
                                  fullWidth
                                  value={bulkImportText}
                                  onChange={(e) => setBulkImportText(e.target.value)}
                                  placeholder="Paste your data here (tab or space separated)"
                                  variant="outlined"
                                  sx={{ mb: 1 }}
                                />
                                <Button variant="contained" color="primary" onClick={handleBulkImport} size="small">
                                  Import Rates
                                </Button>
                              </Box>
                            )}

                            <Divider my={2} />

                            <TableContainer 
                              component={Paper} 
                              sx={{ 
                                mt: 2,
                                maxHeight: "400px",
                                overflowY: "auto"
                              }}
                            >
                              <Table stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>From Rate</TableCell>
                                    <TableCell>To Rate</TableCell>
                                    <TableCell>Month Fee</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rates.map((rate, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{formatWithTwoDecimals(rate.fromRate)}</TableCell>
                                      <TableCell>{formatWithTwoDecimals(rate.toRate)}</TableCell>
                                      <TableCell>{formatWithTwoDecimals(rate.monthFee || 0)}</TableCell>
                                      <TableCell align="right">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRemoveRate(index)}
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow>
                                    <TableCell>
                                      <TextField
                                        type="text"
                                        size="small"
                                        placeholder="From Rate"
                                        value={newRate.fromRate}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Allow numbers, comma, and dot
                                          if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setNewRate({ ...newRate, fromRate: value });
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const normalized = parseDecimalValue(e.target.value);
                                          setNewRate({ ...newRate, fromRate: formatWithTwoDecimals(normalized) });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        type="text"
                                        size="small"
                                        placeholder="To Rate"
                                        value={newRate.toRate}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Allow numbers, comma, and dot
                                          if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setNewRate({ ...newRate, toRate: value });
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const normalized = parseDecimalValue(e.target.value);
                                          setNewRate({ ...newRate, toRate: formatWithTwoDecimals(normalized) });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        type="text"
                                        size="small"
                                        placeholder="Month Fee"
                                        value={newRate.monthFee}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          // Allow numbers, comma, and dot
                                          if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setNewRate({ ...newRate, monthFee: value });
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const normalized = parseDecimalValue(e.target.value);
                                          setNewRate({ ...newRate, monthFee: formatWithTwoDecimals(normalized) });
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        onClick={handleAddRate}
                                        color="primary"
                                        disabled={!newRate.fromRate || !newRate.toRate}
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>

                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              mt={3}
                              my={2}
                              mr={4}
                              disabled={rates.length === 0 || !values.countryId || !values.productId || isSubmitting}
                            >
                              Submit Refund Rates
                            </Button>
                            <Button
                              type="button"
                              variant="contained"
                              color="primary"
                              mt={3}
                              onClick={handleCloseDialog}
                              my={2}
                            >
                              Close
                            </Button>
                          </form>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Formik>
              </DialogContent>
            </Dialog>
          </div>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default AddRefundRatesForm;

